/* A composite is one visual transaction: decode its artwork and frame before
 * committing either image or any geometry. SSR still discovers the first pair. */
function compositeResourceWidth(box, attrs, thumbnail, kind, fit = 0.86) {
  const framed = attrs['render-mode'] === 'overlay_image';
  const stageRatio = Number(attrs[framed ? 'overlay-ar' : 'art-ar']) || 1;
  const scale = thumbnail ? 1 : fit * (Number(attrs['size-scale']) || 1);
  const stageWidth = Math.min(box.width, box.height * stageRatio) * scale;
  if (kind === 'overlay') return stageWidth;
  const sourceRatio = Number(attrs['art-width']) / Number(attrs['art-height']) || Number(attrs['art-ar']) || 1;
  const widthFraction = framed ? (100 - Number(attrs['inset-l'] || 0) - Number(attrs['inset-r'] || 0)) / 100 : 1;
  const heightFraction = framed ? (100 - Number(attrs['inset-t'] || 0) - Number(attrs['inset-b'] || 0)) / 100 : 1;
  return Math.max(stageWidth * widthFraction, stageWidth / stageRatio * heightFraction * sourceRatio);
}

class ArtworkComposite extends HTMLElement {
  static get observedAttributes() {
    return ['render-mode', 'art-src', 'art-srcset', 'art-sizes', 'art-ar', 'art-alt', 'art-width', 'art-height',
      'overlay-src', 'overlay-srcset', 'overlay-ar', 'overlay-width', 'overlay-height',
      'inset-l', 'inset-r', 'inset-t', 'inset-b', 'diagnostic', 'size-scale'];
  }

  static #images = new Map();
  static #decoded = new WeakMap();
  static #owners = new WeakMap();
  static #viewport = [window.innerWidth, window.innerHeight];
  static {
    // Reading innerWidth after selector DOM writes forces a style/layout flush.
    // Viewport resize events keep responsive cache identities current instead.
    window.addEventListener('resize', () => {
      this.#viewport = [window.innerWidth, window.innerHeight];
    }, { passive: true });
  }
  #owner = Symbol('composite-image-owner');
  #stage;
  #art;
  #overlay;
  #version = 0;
  #writing = false;
  #queued = false;
  #ready = Promise.resolve(false);
  #wanted = '';
  #committed = {};
  #logged = new Set();
  #resizeObserver;
  #box;
  #fit = 0.86;

  get ready() { return this.#ready; }
  get renderMode() { return this.getAttribute('render-mode') || 'flat'; }

  connectedCallback() {
    this.#stage = this.querySelector('.composite__stage');
    if (!this.#stage) {
      this.#stage = document.createElement('div');
      this.#stage.className = 'composite__stage';
      this.append(this.#stage);
    }
    this.#art = this.#stage.querySelector('.composite__art');
    this.#overlay = this.#stage.querySelector('.composite__overlay');
    this.#fit = (parseFloat(getComputedStyle(this).getPropertyValue('--fit')) || 86) / 100;
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
        const previous = this.#box;
        this.#box = { width: entry.contentRect.width, height: entry.contentRect.height };
        if (previous && this.#boxChanged(previous) && this.dataset.compositeReady && this.getAttribute('aria-busy') !== 'true') {
          this.#wanted = '';
          this.renderVariant(this.#committed);
        }
      }
    });
    this.#resizeObserver.observe(this);
    this.#committed = this.#attributes();
    // The parser-time bootstrap already handles the first visible pair, including
    // when this module is blocked. Do not replace that pair during hydration.
    this.#ready = this.artworkInitialReady || this.#initialReady();
    if (this.classList.contains('artwork-composite--lightbox')) {
      this.renderVariant({ ...this.#committed, 'art-sizes': '300vw' });
    }
  }

  disconnectedCallback() { this.#version++; this.#wanted = ''; this.#resizeObserver?.disconnect(); this.#box = null; }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#stage || this.#writing || oldValue === newValue || this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      if (this.isConnected) this.renderVariant(this.#attributes());
    });
  }

  #attributes() {
    return Object.fromEntries(ArtworkComposite.observedAttributes.map((name) => [name, this.getAttribute(name)]));
  }

  #boxChanged(previous) {
    return this.#box && (Math.abs(this.#box.width - previous.width) > 0.5 || Math.abs(this.#box.height - previous.height) > 0.5);
  }

  // Shopify's local proxy and public storefront can spell the same CDN asset
  // with different hosts. Only identify /cdn/shop/files assets this way; never
  // rewrite arbitrary external images or ignore the file version.
  static #identity(src) {
    if (!src) return '';
    const url = new URL(src, document.baseURI);
    if (!url.pathname.startsWith('/cdn/shop/files/')) return url.href;
    url.searchParams.delete('width');
    return url.pathname + url.search;
  }

  #descriptor(attrs, kind) {
    const image = kind === 'art' ? this.#art : this.#overlay;
    const src = attrs[`${kind}-src`] ?? image?.getAttribute('src') ?? '';
    const zoom = this.classList.contains('artwork-composite--lightbox');
    let srcset = attrs[`${kind}-srcset`] ?? image?.getAttribute('srcset') ?? '';
    let sizes = kind === 'art' && zoom ? '300vw' : (attrs[`${kind}-sizes`] || image?.sizes || '');
    let requiredWidth = 0;
    if (zoom && kind === 'overlay') {
      srcset = '';
      sizes = '';
    } else if (!zoom && this.#box && (this.classList.contains('product-gallery__composite') || this.classList.contains('product-gallery__composite-thumbnail'))) {
      requiredWidth = compositeResourceWidth(this.#box, attrs, this.classList.contains('product-gallery__composite-thumbnail'), kind, this.#fit);
      if (requiredWidth > 0) sizes = `${Math.ceil(requiredWidth)}px`;
    }
    // Preserve SSR's already selected resource for the same artwork, including
    // the thumbnail's smaller srcset. Zoom deliberately requests a larger set.
    const same = ArtworkComposite.#identity(src) === ArtworkComposite.#identity(image?.getAttribute('src'));
    let sufficient = false;
    if (requiredWidth > 0 && image?.complete && image.naturalWidth > 0) {
      const rasterWidth = Number(new URL(image.currentSrc || image.src, document.baseURI).searchParams.get('width'));
      sufficient = rasterWidth > 0 && rasterWidth >= requiredWidth * window.devicePixelRatio;
    }
    const reuse = same && (sizes === (image?.sizes || '') || sufficient) && !!image && !(image.complete && !image.naturalWidth) && !(zoom && kind === 'overlay' && image.getAttribute('srcset'));
    if (reuse) srcset = image.getAttribute('srcset') || '';
    return {
      src: reuse ? image.getAttribute('src') : src, srcset, sizes: reuse ? image.sizes || '' : sizes, reuse: reuse ? image : null,
      retain: !zoom
    };
  }

  static async #decode(image) {
    if (!image || !(image.getAttribute('src') || image.getAttribute('srcset'))) throw new Error('missing-image');
    const identity = () => JSON.stringify([
      image.currentSrc, image.getAttribute('src'), image.getAttribute('srcset'), image.sizes,
      ...this.#viewport, window.devicePixelRatio
    ]);
    const known = this.#decoded.get(image);
    // A size-only variant change keeps the same already-decoded visible node.
    // Repeating decode() would unnecessarily wait for another rendering turn.
    // complete alone is never sufficient: require our successful decode record
    // and an unchanged responsive candidate, attributes and viewport as well.
    if (known?.identity === identity() && image.complete && image.naturalWidth > 0) return known.promise;
    try {
      await image.decode();
      if (!image.naturalWidth) throw new Error('broken-image');
      this.#decoded.set(image, { identity: identity(), promise: Promise.resolve(image) });
      return image;
    } catch (error) {
      this.#decoded.delete(image);
      throw error;
    }
  }

  static #load(descriptor, priority) {
    const key = JSON.stringify([descriptor.src, descriptor.srcset, descriptor.sizes, window.devicePixelRatio, ...this.#viewport]);
    let entry = this.#images.get(key);
    if (entry) {
      if (priority === 'high') entry.image.fetchPriority = 'high';
      entry.retain = entry.retain && descriptor.retain !== false;
      this.#images.delete(key);
      if (!entry.settled || entry.retain) this.#images.set(key, entry);
      return entry.promise;
    }
    const image = new Image();
    image.decoding = 'async';
    image.loading = 'eager';
    image.fetchPriority = priority;
    if (descriptor.sizes) image.sizes = descriptor.sizes;
    if (descriptor.srcset) image.srcset = descriptor.srcset;
    image.src = descriptor.src;
    entry = { image, settled: false, retain: descriptor.retain !== false, pixels: 0, promise: null };
    entry.promise = this.#decode(image).then(() => image).catch((error) => {
      if (this.#images.get(key) === entry) this.#images.delete(key); // Failed loads remain retryable.
      throw error;
    }).finally(() => {
      entry.settled = true;
      entry.pixels = this.#pixelEstimate(image);
      // A zoom original is useful to its active clone and the browser's HTTP
      // cache, but must not remain pinned by this page-level decoded warm pool.
      if ((!entry.retain || entry.pixels > 6_000_000) && this.#images.get(key) === entry) this.#images.delete(key);
      for (const [retainedKey, retained] of this.#images) {
        if (!retained.settled) continue;
        // A responsive cached node can select a larger raster after resize.
        retained.pixels = this.#pixelEstimate(retained.image);
        if (retained.pixels > 6_000_000) this.#images.delete(retainedKey);
      }
      const settled = [...this.#images.values()].filter((item) => item.settled);
      let pixels = settled.reduce((total, item) => total + item.pixels, 0);
      let count = settled.length;
      for (const [oldKey, oldEntry] of this.#images) {
        if (count <= 16 && pixels <= 24_000_000) break;
        if (!oldEntry.settled) continue;
        this.#images.delete(oldKey);
        pixels -= oldEntry.pixels;
        count--;
      }
    });
    this.#images.set(key, entry);
    return entry.promise;
  }

  static #pixelEstimate(image) {
    // naturalWidth can be density-corrected for srcset. Shopify's selected URL
    // width estimates raster width; the intrinsic ratio gives its raster height.
    let width = image.naturalWidth || 0;
    try {
      const requested = Number(new URL(image.currentSrc || image.src, document.baseURI).searchParams.get('width'));
      if (Number.isFinite(requested) && requested > 0) width = Math.max(width, requested);
    } catch { /* Non-URL/custom image sources use the intrinsic dimensions. */ }
    const ratio = image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : 1;
    return Math.ceil(width * width / (ratio > 0 ? ratio : 1));
  }

  async preloadVariant(attrs, { priority = 'low' } = {}) {
    if (!this.#stage) return false;
    const state = { ...this.#committed, ...attrs };
    const descriptors = [this.#descriptor(state, 'art')];
    if (state['render-mode'] === 'overlay_image' && state['overlay-src']) descriptors.push(this.#descriptor(state, 'overlay'));
    // Keep the scheduler's pair slot until both halves finish, even when one
    // fails early and its partner is still downloading.
    const results = await Promise.allSettled(descriptors.map((d) => d.reuse ? ArtworkComposite.#decode(d.reuse) : ArtworkComposite.#load(d, priority)));
    return results.every((result) => result.status === 'fulfilled');
  }

  renderVariant(attrs) {
    if (!this.#stage) return Promise.resolve(false);
    const state = { ...this.#committed, ...attrs };
    const signature = JSON.stringify(state);
    if (signature === this.#wanted) return this.#ready;
    this.#wanted = signature;
    const version = ++this.#version;
    const measuredBox = this.#box;
    const started = performance.now();
    const art = this.#descriptor(state, 'art');
    const framed = state['render-mode'] === 'overlay_image' && !!state['overlay-src'];
    const overlay = framed ? this.#descriptor(state, 'overlay') : null;
    this.setAttribute('aria-busy', 'true');
    const prepare = async (descriptor, className) => {
      if (descriptor.reuse) return ArtworkComposite.#decode(descriptor.reuse);
      const cached = await ArtworkComposite.#load(descriptor, 'high');
      // A warmed node is already decoded. Its first composite can use those
      // pixels directly and reuse the same node on a later return to this crop.
      // Symbols do not retain component instances; other composites must clone,
      // so a thumbnail or lightbox can never steal the gallery's live image.
      const owner = ArtworkComposite.#owners.get(cached);
      if (!owner || owner === this.#owner) {
        ArtworkComposite.#owners.set(cached, this.#owner);
        cached.className = className;
        return ArtworkComposite.#decode(cached);
      }
      const image = cached.cloneNode();
      image.className = className;
      await ArtworkComposite.#decode(image);
      return image;
    };
    this.#ready = Promise.all([prepare(art, 'composite__art'), overlay ? prepare(overlay, 'composite__overlay') : null]).then(([nextArt, nextOverlay]) => {
      if (version !== this.#version || !this.isConnected) return false;
      this.#writing = true;
      for (const [name, value] of Object.entries(state)) {
        if (value === null || value === undefined) this.removeAttribute(name);
        else this.setAttribute(name, value);
      }
      this.#writing = false;
      nextArt.alt = state['art-alt'] || nextArt.alt || '';
      if (nextOverlay) nextOverlay.alt = '';
      // One task, no animation: decoded pixels and every geometry input become
      // visible at the same rendering opportunity.
      this.#stage.replaceChildren(...[nextArt, nextOverlay].filter(Boolean));
      this.#art = nextArt;
      this.#overlay = nextOverlay;
      this.dataset.renderMode = state['render-mode'] || 'flat';
      for (const name of ['art-ar', 'overlay-ar', 'size-scale', 'inset-l', 'inset-r', 'inset-t', 'inset-b']) {
        if (state[name] !== null && state[name] !== undefined) this.style.setProperty(`--${name}`, state[name]);
        else this.style.removeProperty(`--${name}`);
      }
      this.#committed = state;
      delete this.dataset.compositePending;
      delete this.dataset.compositeError;
      this.querySelector('.composite__fallback')?.remove();
      this.setAttribute('aria-busy', 'false');
      this.dataset.compositeReady = 'true';
      this.#diagnose(state.diagnostic);
      this.dispatchEvent(new CustomEvent('artwork-composite:commit', { bubbles: true, detail: {
        duration: performance.now() - started, art: nextArt.currentSrc, overlay: nextOverlay?.currentSrc || null
      } }));
      if (measuredBox && this.#boxChanged(measuredBox)) {
        queueMicrotask(() => {
          if (version !== this.#version || !this.isConnected) return;
          this.#wanted = '';
          this.renderVariant(this.#committed);
        });
      }
      return true;
    }).catch(() => {
      if (version !== this.#version) return false;
      this.#wanted = ''; // Selecting this option again retries the failed load.
      this.setAttribute('aria-busy', 'false');
      this.dataset.compositeError = 'image-load';
      this.dispatchEvent(new CustomEvent('artwork-composite:error', { bubbles: true }));
      return false;
    });
    return this.#ready;
  }

  #initialReady() {
    const images = [this.#art, this.dataset.renderMode === 'overlay_image' ? this.#overlay : null].filter(Boolean);
    return Promise.all(images.map((image) => ArtworkComposite.#decode(image))).then(() => {
      delete this.dataset.compositePending;
      this.dataset.compositeReady = 'true';
      return true;
    }).catch(() => false);
  }

  #diagnose(value) {
    const codes = (value || '').split(/\s+/).filter(Boolean);
    for (const code of codes) {
      if (this.#logged.has(code)) continue;
      this.#logged.add(code);
      console.warn(`[artwork-composite] ${code}`, this);
    }
    if (codes.length) this.dataset.compositeDiagnostic = codes.join(' ');
    else delete this.dataset.compositeDiagnostic;
  }
}

if (!customElements.get('artwork-composite')) customElements.define('artwork-composite', ArtworkComposite);


class ArtworkCompositeZoom {
  static #wired = new WeakSet();

  static wire(gallery) {
    if (!gallery || ArtworkCompositeZoom.#wired.has(gallery)) return;
    ArtworkCompositeZoom.#wired.add(gallery);
    const zoom = new ArtworkCompositeZoom(gallery);
    zoom.listen();
  }

  #gallery;
  #cell;
  #liveClone = null;
  #resizeView = null;

  constructor(gallery) {
    this.#gallery = gallery;
    this.#cell = gallery.querySelector('.product-gallery__media--composite');
  }

  listen() {
    if (!this.#cell || !this.#gallery.hasAttribute('allow-zoom')) return;
    // 1. desktop click on the composite's artwork image
    this.#cell.addEventListener('click', (event) => {
      if (!window.matchMedia('(min-width: 1000px)').matches) return;
      if (!(event.target instanceof HTMLImageElement)) return;
      event.stopPropagation();
      this.open();
    }, { capture: true });
    // 2. the mobile zoom button and anything else that asks the gallery to open
    this.#gallery.addEventListener('lightbox:open', (event) => {
      const selected = this.#gallery.carousel?.selectedCell;
      const index = event.detail?.index;
      if (index === undefined && selected !== this.#cell) return;
      if (index !== undefined && index !== -1) return;
      event.stopImmediatePropagation();
      this.open();
    }, { capture: true });
    // 3. iOS pinch on the composite cell (Prestige ignores non-image cells)
    this.#cell.addEventListener('gesturechange', (event) => {
      if (event.scale > 1.5) { event.preventDefault(); this.open(); }
    });
  }

  #dataSource() {
    this.#gallery.restoreDeferredArtworkMedia?.();
    const cells = this.#gallery.carousel?.cells ?? [];
    const items = [];
    for (const cell of cells) {
      if (cell === this.#cell) {
        const composite = cell.querySelector('artwork-composite');
        const art = composite?.querySelector('.composite__art');
        const geometry = this.#geometry(composite);
        if (geometry) items.push({ composite: true, ...geometry, alt: art?.alt || '', thumbnailElement: art });
        continue;
      }
      for (const image of cell.querySelectorAll(':scope > img, :scope > picture.vrp-responsive-image > img')) {
        items.push({
          thumbnailElement: image, src: image.src, srcset: image.srcset, msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')), height: parseInt(image.getAttribute('height')), alt: image.alt, thumbCropped: true
        });
      }
    }
    return items;
  }

  #geometry(composite) {
    if (!composite) return null;
    const framed = composite.dataset.renderMode === 'overlay_image';
    const image = composite.querySelector(framed ? '.composite__overlay' : '.composite__art');
    const kind = framed ? 'overlay' : 'art';
    const sourceWidth = Number(composite.getAttribute(`${kind}-width`)) || Number(image?.getAttribute('width'));
    const sourceHeight = Number(composite.getAttribute(`${kind}-height`)) || Number(image?.getAttribute('height'));
    const [numerator, denominator = 1] = getComputedStyle(composite.querySelector('.composite__stage')).aspectRatio.split('/').map(Number);
    const ratio = numerator / denominator || Number(composite.getAttribute(`${kind}-ar`));
    if (!(sourceWidth > 0 && sourceHeight > 0 && ratio > 0) || !Number.isFinite(sourceWidth + sourceHeight + ratio)) return null;
    // Display naturalWidth is density-corrected by sizes. Zoom instead uses the
    // source/cropped face; cover cannot enlarge either source axis past native.
    const width = framed ? Math.round(sourceWidth) : Math.min(sourceWidth, sourceHeight * ratio);
    return { framed, width, height: framed ? Math.round(width / ratio) : width / ratio };
  }

  open() {
    const lightbox = this.#gallery.lightBox;
    if (!lightbox) return;
    if (!lightbox.__artworkCompositeHooked) {
      lightbox.__artworkCompositeHooked = true;
      lightbox.on('contentLoad', (event) => {
        if (!event.content.data.composite) return;
        event.preventDefault();
        event.content.element = this.#cloneForLightbox();
      });
      lightbox.addFilter('isContentZoomable', (zoomable, content) => (content.data.composite ? true : zoomable));
      // Framed: zoom stops at the overlay's native pixels; a function option resolves per slide.
      const stockMax = lightbox.options.maxZoomLevel;
      const magnification = Math.max(1, parseInt(this.#gallery.getAttribute('allow-zoom')) || 3);
      lightbox.options.maxZoomLevel = (zoomLevel) => {
        const data = zoomLevel.itemData || {};
        if (data.composite && data.framed) return Math.max(zoomLevel.fit, 1);
        if (data.composite) return Math.min(1, zoomLevel.fit * magnification);
        return typeof stockMax === 'function' ? stockMax(zoomLevel) : stockMax;
      };
      const stockSecondary = lightbox.options.secondaryZoomLevel;
      lightbox.options.secondaryZoomLevel = (zoomLevel) => {
        if (zoomLevel.itemData?.composite && !zoomLevel.itemData.framed) return Math.min(1, zoomLevel.fit * magnification);
        return typeof stockSecondary === 'function' ? stockSecondary(zoomLevel) : stockSecondary;
      };
      // Room around a framed piece at the first zoom level (an edge-to-edge frame reads as
      // cropped): 6% of the viewport, at least 24 px, on composite slides only. Photos keep Prestige's own padding (paddingFn, then padding; the
      // per-side paddingLeft/Top/Right/Bottom form is not carried, Prestige sets none).
      const stockPad = lightbox.options.paddingFn;
      const stockPadding = lightbox.options.padding;
      lightbox.options.paddingFn = (viewportSize, itemData, index) => {
        if (itemData?.composite) { const pad = Math.max(24, Math.round(Math.min(viewportSize.x, viewportSize.y) * 0.06)); return { top: pad, bottom: pad, left: pad, right: pad }; }
        if (typeof stockPad === 'function') return stockPad(viewportSize, itemData, index);
        return stockPadding || { top: 0, bottom: 0, left: 0, right: 0 };
      };
      lightbox.on('beforeResize', () => { this.#resizeView = this.#captureView(lightbox.pswp?.currSlide); });
      lightbox.on('resize', () => {
        const view = this.#resizeView;
        this.#resizeView = null;
        if (view && lightbox.pswp?.currSlide === view.slide) {
          view.slide.calculateSize();
          this.#restoreView(view);
        }
      });
      // PhotoSwipe updates currSlide after slideActivate, before change.
      lightbox.on('change', () => {
        const clone = this.#liveClone?.querySelector('artwork-composite');
        if (clone) this.#refit(clone);
      });
      // PhotoSwipe starts listening for window resize only after opening.
      lightbox.on('openingAnimationEnd', () => {
        const pswp = lightbox.pswp;
        if (pswp?.currSlide?.data.composite) pswp.updateSize();
      });
      lightbox.on('close', () => { this.#liveClone?.remove(); this.#liveClone = null; this.#resizeView = null; });
    }
    const dataSource = this.#dataSource();
    const index = dataSource.findIndex((item) => item.composite);
    if (index >= 0) lightbox.loadAndOpen(index, dataSource);
  }

  #cloneForLightbox() {
    const source = this.#cell.querySelector('artwork-composite');
    const wrapper = document.createElement('div');
    wrapper.className = 'pswp__composite';
    const clone = source.cloneNode(true);
    clone.removeAttribute('id');
    clone.classList.remove('product-gallery__composite');
    clone.classList.add('artwork-composite--lightbox');
    const art = clone.querySelector('.composite__art');
    if (art) { art.loading = 'eager'; }
    clone.setAttribute('art-sizes', '300vw');
    wrapper.append(clone);
    this.#liveClone = wrapper;
    // The selector drives the clone too; an aspect change re-fits the slide box.
    clone.addEventListener('artwork-composite:commit', () => this.#refit(clone));
    return wrapper;
  }

  #refit(clone) {
    const pswp = this.#gallery.lightBox?.pswp;
    const slide = pswp?.currSlide;
    if (!slide?.data.composite || slide.content.element !== this.#liveClone || !this.#liveClone?.contains(clone)) return;
    const geometry = this.#geometry(clone);
    if (!geometry || (slide.width === geometry.width && slide.height === geometry.height && slide.data.framed === geometry.framed)) return;
    const view = this.#captureView(slide);
    // PhotoSwipe 5.4.4 caches dimensions in Content and Slide, separately from
    // item data. Keep the live clone and synchronize all three before sizing.
    Object.assign(slide.data, geometry);
    Object.assign(slide.content, { width: geometry.width, height: geometry.height });
    Object.assign(slide, { width: geometry.width, height: geometry.height });
    slide.calculateSize();
    if (view) this.#restoreView(view);
  }

  #captureView(slide) {
    if (!slide?.data.composite || !(slide.currZoomLevel > 0 && slide.zoomLevels.initial > 0)) return null;
    const center = slide.pswp.getViewportCenterPoint();
    return {
      slide,
      magnification: slide.currZoomLevel / slide.zoomLevels.initial,
      x: (center.x - slide.pan.x) / (slide.width * slide.currZoomLevel),
      y: (center.y - slide.pan.y) / (slide.height * slide.currZoomLevel)
    };
  }

  #restoreView(view) {
    const { slide } = view;
    const { pswp, zoomLevels } = slide;
    pswp.animations.stopAllPan();
    const level = Math.min(zoomLevels.max, Math.max(zoomLevels.initial, zoomLevels.initial * view.magnification));
    slide.setZoomLevel(level);
    // Reset the content's resolution basis at the same time as its dimensions;
    // otherwise PhotoSwipe's transform scales a wrapper with stale pixel sizes.
    slide.currentResolution = level;
    slide.updateContentSize(true);
    const center = pswp.getViewportCenterPoint();
    const atInitial = Math.abs(level - zoomLevels.initial) < 0.000001;
    slide.panTo(
      atInitial ? slide.bounds.center.x : center.x - view.x * slide.width * level,
      atInitial ? slide.bounds.center.y : center.y - view.y * slide.height * level
    );
  }
}

const wireGalleries = () => {
  for (const gallery of document.querySelectorAll('product-gallery')) ArtworkCompositeZoom.wire(gallery);
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wireGalleries, { once: true });
else wireGalleries();
document.addEventListener('shopify:section:load', wireGalleries);
window.ArtworkCompositeZoom = ArtworkCompositeZoom;
